// ─── Authentication ──────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
  tenantId: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface TokenPayload {
  sub: string;
  tenantId: string;
  /** OP-367 — human-readable tenant name claim; absent on older tokens. */
  tenantName?: string;
  email: string;
  displayName: string;
  role: string;
  exp: number;
  iat: number;
}

export interface UserInfo {
  agentId: string;
  tenantId: string;
  /** OP-367 — tenant display name for the console header; falls back to tenantId when absent. */
  tenantName?: string;
  email: string;
  displayName: string;
  role: string;
}

// ─── Agent ───────────────────────────────────────────────────────

export type AgentAvailability = 'ONLINE' | 'BUSY' | 'OFFLINE';

export interface AgentInfo {
  agentId: string;
  displayName: string;
  availability: AgentAvailability;
  connectedAt: string;
}

/**
 * ADR-016 §D3 — the caller's RESOLVED per-agent concurrency cap, returned by
 * GET /api/agent-profiles/{agentId}. `maxConcurrentConversations` is 0–10
 * (0 = do not route new conversations to this agent). Resolved server-side:
 * an agent with no stored profile gets the tenant/config default.
 */
export interface AgentProfile {
  agentId: string;
  maxConcurrentConversations: number;
}

/**
 * One entry from GET /api/identity/agent-directory — an id→name pair for a staff user in the
 * caller's tenant, used to render a conversation's owner as a person rather than a GUID.
 *
 * Intentionally only these two fields: the endpoint is gated by `workspace.view`, which every staff
 * role holds, so it deliberately does NOT serve the email / lock-state / last-login that the
 * admin-only user list carries.
 */
export interface AgentDirectoryEntryDto {
  agentId: string;
  displayName: string;
}

/**
 * ADR-016 §D9 — the caller's OWN current load, returned by
 * GET /api/escalation/my-load. `openConversations` is how many conversations
 * the agent is presently on the hook for; paired with {@link AgentProfile} it
 * drives the read-only "load / cap" badge next to the availability selector.
 */
export interface AgentLoad {
  agentId: string;
  openConversations: number;
}

// ─── Conversation / Session ──────────────────────────────────────

export type SessionState =
  | 'AI_HANDLING'
  | 'CONSULTATION_REQUESTED'
  | 'HUMAN_TAKEOVER'
  | 'CLOSED';

export type SlaUrgency = 'CRITICAL' | 'WARNING' | 'NORMAL';

export interface Conversation {
  sessionId: string;
  tenantId: string;
  customerId: string;
  /** Identified customer's name (W8); used as the list label when present. */
  customerDisplayName?: string;
  /** First message preview (W8); used for the "Anonymous - {first message}" label. */
  firstMessagePreview?: string;
  /**
   * Staff-set NAME of this conversation, from <see cref="SessionDto.subject"/>.
   *
   * Null/undefined is the NORMAL case, not missing data: nobody has named it, so the UI falls
   * back to <c>getConversationLabel</c>'s per-channel derivation. Unlike
   * <c>lastMessageContent</c> (which moves with every message) a subject is stable once set.
   * Rows synthesised from a live SignalR event leave this undefined until the next list load.
   */
  subject?: string | null;
  channel: string;
  state: SessionState;
  slaUrgency: SlaUrgency;
  slaDeadlineUtc: string;
  assignedAgentId?: string;
  /**
   * The private owner of this conversation (ADR-033), from the wire `ownerAgentId`. THE owner: there is no fallback to
   * `assignedAgentId` any more (that field is the live controller / the consultation's addressee, not an owner).
   *
   * Three values, and they are not the same: a string = owned by that staff id; `null` = the server said "nobody owns
   * it" (team shared); `undefined` = the server did NOT say (a row synthesised from a live event, or an old server) -
   * unknown, which every consumer fails closed on. The console never decides ownership; it renders this.
   */
  ownerAgentId?: string | null;
  /** When the current owner claimed it (ISO-8601 UTC); null/undefined when unowned or unknown. */
  ownerSince?: string | null;
  /** How the owner came to own it: 'guidance' | 'takeover' | 'draft_approve' | 'backfill' (any string the server adds later). */
  ownerSource?: string | null;
  /**
   * The owner let go of the live conversation (released it back to the bot) but KEPT ownership of it - the "Released to
   * bot" folder. Absent = false.
   */
  ownerReleased?: boolean;
  /** Monotonic ownership version; a push older than the row's is ignored. Absent = 0 (unknown). */
  ownerVersion?: number;
  /**
   * Is private ownership IN EFFECT for this tenant, as the server last said (list envelope or row). FALSE for every
   * tenant while PR-A1 is what is deployed: the console then renders legacy shared behaviour (no privacy promise,
   * everyone may act). Absent = false. Keyed on this wire value ONLY, never on a config key.
   */
  ownershipEnforced?: boolean;
  /** Existing catalog tag KEYS attached to the conversation (the console attaches/detaches existing ones only). Absent = []. */
  tags?: string[];
  /** Monotonic tag version; a tags push is applied only when newer. Absent = 0. */
  tagsVersion?: number;
  /** Last activity of any kind (ISO-8601 UTC) - the list's sort key. Absent = fall back to `lastMessageTimestamp`. */
  lastActivityAt?: string;
  /** Last customer message (ISO-8601 UTC); drives "Customer waiting". */
  lastCustomerMessageAt?: string | null;
  /** Last outbound (bot or agent) message (ISO-8601 UTC). */
  lastOutboundAt?: string | null;
  /** The customer's previous CLOSED conversation, when the server linked one (D7); null/undefined = none. */
  previousSessionId?: string | null;
  /**
   * True for a row built from a push or a single-row read rather than a full list row, so list-only fields (counts,
   * owner...) are not known. Never set by `mapSessionToConversation`.
   */
  partial?: boolean;
  lastMessageContent: string;
  lastMessageTimestamp: string;
  unreadCount: number;
  createdAt: string;
  /**
   * UTC timestamp the session closed, from <see cref="SessionDto.closedAt"/>; undefined while it
   * is still open. Shown in the Conversation panel only when <c>state === 'CLOSED'</c>.
   */
  closedAt?: string | null;
  /**
   * Mirrors <see cref="SessionDto.messageCount"/> — messages recorded so far
   * (either direction). Undefined/0 means no activity yet. A row created live
   * from a bare SessionCreated event starts at 0 and is bumped by
   * handleMessageNotification/handleOutboundMessage on the first real message;
   * ConversationList hides messageCount === 0 rows from every folder so a
   * customer who merely opened the widget doesn't clutter the inbox.
   */
  messageCount?: number;
  /**
   * Free-form per-channel data (e.g. <c>emailSubject</c> for Email,
   * <c>displayName</c> for WhatsApp/SMS). Populated from
   * <see cref="SessionDto.metadata"/>. Used by <c>getConversationLabel</c>
   * to render channel-appropriate conversation headers.
   */
  metadata: Record<string, string>;

  /**
   * ADR-017 D3 — the per-tenant conversation-state (Category) key this session
   * currently sits under; drives which console directory section it renders in.
   * Carried from <see cref="SessionDto.category"/>; null/undefined ⇒ the trailing
   * "Uncategorized" fallback section. Independent of <c>state</c> (the 5-value
   * lifecycle) — a parallel classification axis, never a lifecycle driver.
   */
  category?: string | null;

  /**
   * BotId of the open consultation, if any. Set by <c>handleConsultation</c>
   * when a CONSULTATION_REQUESTED arrives, cleared by <c>handleSessionStateChanged</c>
   * when the session transitions back out. The "Let bot continue" button needs
   * this to call DismissConsultation; the channel comes from <c>channel</c>.
   */
  consultationBotId?: string;

  /**
   * ADR-016 §D8 — true when the OPEN consultation on this row was routed to
   * ANOTHER agent (this console received only the awareness copy). Drives the
   * read-only "Assigned to another agent" treatment in the panel. Cleared by
   * <c>handleSessionStateChanged</c> when the session leaves the consultation
   * state. Absent/false ⇒ claimable (routing off, or this is the assignee).
   */
  assignedToOther?: boolean;

  /**
   * ADR-016 §D8 — X-Transaction-Id of the open consultation. Used to dedupe
   * the two delivered copies (actionable + awareness) on
   * <c>(sessionId, transactionId)</c>, PREFERRING the actionable copy so an
   * assignee who receives both never gets downgraded to read-only.
   */
  consultationTransactionId?: string;

  /**
   * 2026-10-28 - WHY the bot last consulted the team on this conversation: the `reasonCode` of the newest
   * `consultation_requested` timeline entry this console has seen for it (a `ConsultationReasons` value such as
   * `CustomerKeyword`), and when that entry happened. Console-only decoration: the session list does NOT carry it, so it
   * is learned from the timeline (the open conversation's page, and the live `ReceiveTimelineEntry` push for any row).
   * Cleared when the row leaves CONSULTATION_REQUESTED. Read it through `utils/consultationReason.ts`, never directly.
   */
  consultationReason?: { code: string; at: string };

  /** ADR-019 — the email reply draft awaiting this row's review, if any. */
  pendingDraft?: PendingEmailDraft;

  /** Conversation-status overhaul: the conversation's priority. Absent = MEDIUM (an old row). */
  priority?: ConversationPriority;
  /** True once a person set the priority by hand. */
  priorityPinned?: boolean;
  /** Text of the NEWEST message from any side (about 200 chars); null/undefined = the server did not say (fall back to `lastMessageContent`). */
  lastPreview?: string | null;
  lastPreviewAt?: string | null;
  /** Who wrote the newest message. null/undefined = unknown (the preview then reads as a customer message). */
  lastPreviewSender?: PreviewSender | null;
  /** Display name of the human agent when the sender is 'agent'. */
  lastPreviewAgentName?: string | null;
  /** When this conversation was last reopened (ISO-8601 UTC); null = never. */
  reopenedAt?: string | null;
  reopenCount?: number;
}

/** Conversation priority (POST /api/sessions/{id}/priority). */
export type ConversationPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export const CONVERSATION_PRIORITIES: readonly ConversationPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
/** Who wrote the newest message of a conversation. */
export type PreviewSender = 'customer' | 'bot' | 'agent';

/** ADR-017 D6 — a per-locale label carried on a conversation-state definition. */
export interface LocalizedLabel {
  en: string;
  he: string;
}

/**
 * ADR-017 D1/D3 — a per-tenant conversation-state ("Category") definition. The
 * console fetches the tenant's defs once (GET /api/sessions/state-defs) to render
 * the directory section headers: <c>label</c> in the active language, the
 * <c>color</c> chip, and the section <c>order</c>. Held in the conversationStore
 * as a key→def map. Extra server fields (createdAt/updatedAt) are ignored here.
 */
export interface ConversationStateDef {
  id: string;
  tenantId: string;
  /** Stable slug, tenant-unique — matches <see cref="Conversation.category"/>. */
  key: string;
  label: LocalizedLabel;
  /** Hex colour for the section chip. */
  color: string;
  /** Section display order (ascending). */
  order: number;
  /** One of NEW | ACTIVE | CONSULTING | HUMAN_CONTROLLED | CLOSED — advisory only. */
  mapsToLifecycle: string;
  /** "pipelineId::stageId" for outbound HubSpot sync, or null = console-only. */
  crmStageRef: string | null;
  /** The category a new/uncategorised session shows under. */
  isDefault: boolean;
  /** ADR-017 phase 2 auto-categorisation trigger binding, or null = manual-only. */
  autoTrigger?: string | null;
  /** Soft-disable without deleting (existing sessions keep the key). */
  isActive: boolean;
  /**
   * Every tenant has exactly ONE system status (autoTrigger "closed", mapsToLifecycle "CLOSED"): choosing it closes the
   * conversation on the server. Its label, colour and order are editable; it cannot be deleted or deactivated.
   */
  isSystem?: boolean;
}

// ─── Messages ────────────────────────────────────────────────────

export type MessageSender = 'CUSTOMER' | 'AI' | 'AGENT';

/**
 * WS-E.4 — a single file attached to a message. The backend shape
 * (MediaAttachmentService) is landing on a parallel stream; every field
 * beyond the identity/display basics is optional so the UI stays
 * safe when it is absent (today's norm) or partially populated.
 * Bytes are fetched separately via GET /api/media/{fileId}/content.
 */
export interface MessageAttachment {
  fileId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind?: string;
  scanStatus?: string;
  hasExtractedText?: boolean;
  /**
   * 2026-09-05 - which service owns the bytes: `conversation` (MediaAttachmentService, the default and
   * the only value before this field existed) or `knowledge` (KnowledgeBaseService: a bank image or
   * article file the bot attached; `fileId` is then a content hash). Drives the fetch route.
   */
  plane?: 'conversation' | 'knowledge';
}

/** See {@link Message.stream}. */
export type MessageStream = 'chat' | 'consult';

export interface Message {
  messageId: string;
  sessionId: string;
  sender: MessageSender;
  senderId: string;
  content: string;
  contentType: string;
  timestamp: string;
  /**
   * HC2.2 / OP-061 — optional discriminator for synthetic entries injected
   * into the Bot Consultation pane that are NOT chat messages but system
   * alerts (e.g. "the bot couldn't deliver your guidance"). When set to
   * <c>'alert'</c> the consultation pane renders a distinct role="alert"
   * box instead of a normal MessageBubble. Absent for ordinary messages.
   *
   * <c>'system'</c> (chat action timeline) is a staff-only line BETWEEN the messages - "Dana took
   * over", "Bot consulted the team" - built from a {@link TimelineEntry} carried in {@link timeline}.
   * It is never a message: it has no sender, no content and never reaches the customer.
   */
  kind?: 'alert' | 'system';
  /**
   * Which conversation this row belongs to in the ONE merged timeline: `'chat'` is the customer-facing
   * conversation, `'consult'` is the internal bot<->staff consultation (a bot note, a teammate's guidance,
   * a delivery-failure alert). Absent means `'chat'`. Timeline rows (`kind: 'system'`) carry no stream; they
   * are staff-only lines BETWEEN the messages and are not counted as either.
   */
  stream?: MessageStream;
  /** The timeline entry behind a `kind: 'system'` row; absent on every other row. */
  timeline?: TimelineEntry;
  /** WS-E.4 — files attached to this message. Absent is the norm today. */
  attachments?: MessageAttachment[];
  /**
   * OP-681 - this outbound reply definitely did NOT reach the customer.
   *
   * Absent means "nobody has said", which is the normal case and is NOT the same as delivered. Only
   * `true` is a statement, and it is set solely from the `ReceiveOutboundDeliveryFailed` push. There
   * is deliberately no `delivered` flag here: pending/sent/delivered/seen is the status ladder and
   * belongs to the work that owns it.
   */
  deliveryFailed?: boolean;
  /** 2026-10-31 - the turn that wrote this row (from the staff transcript route); absent on live-pushed rows. */
  transactionId?: string;
  /**
   * 2026-11-01 - on a web-chat CUSTOMER row: the page of the tenant's site the customer was on when they sent it (staff
   * transcript route only). A CLIENT CLAIM, rendered as plain text only and never acted on. Absent when unknown (email,
   * WhatsApp, old rows, live-pushed rows).
   */
  /** Operator 2026-11-02: the public name of the bot that wrote an AI row (server-recorded); absent = "Bot". */
  botName?: string;
  pagePath?: string;
  /** 2026-11-01 - the page title the customer saw. Client claim, display only; see {@link pagePath}. */
  pageTitle?: string;
  /** Stable reason code behind `deliveryFailed` (see OutboundFailureReasons on the server). */
  deliveryFailureReason?: string;
  /**
   * 2026-10-28 - on a BOT consultation note only: up to three replies the bot proposes the agent could send to the
   * customer. STAFF-ONLY. The text is shown, never sent from here: sending one invokes `SendProposedReply(sessionId,
   * noteMessageId, index)` and the server sends ITS stored text. The position in this array IS the index the server
   * expects, so the array is kept exactly as the server sent it (never filtered or reordered).
   */
  proposedReplies?: string[];
}

// ─── SignalR Notifications ───────────────────────────────────────

/**
 * 2026-10-28 - `ReceiveConsultationProposedReplies` on the staff hub: the replies the bot proposes under the
 * consultation note `noteMessageId` (= that note's messageId, `bot-consult-{sessionId}-{transactionId}`). It can
 * arrive a few seconds AFTER the note, or before the note is on screen. Staff-only.
 */
export interface ConsultationProposedRepliesNotification {
  sessionId: string;
  noteMessageId: string;
  replies: string[];
}

/**
 * OP-681 - an outbound reply definitely did not reach the customer.
 *
 * The console used to report every outbound message as sent unconditionally, so an agent who took over
 * an email or WhatsApp conversation watched their reply appear in the thread with a delivered tick
 * while nothing had been transmitted. This is the only push that contradicts that.
 */
export interface OutboundDeliveryFailedNotification {
  messageId: string;
  sessionId: string;
  /** "email" | "whatsapp" - a display hint, never an address. */
  channel: string;
  /** Stable reason code; unknown values fall back to a generic "not delivered" in the UI. */
  reason: string;
  wasHumanAuthored: boolean;
  timestamp: string;
}

export interface ConsultationNotification {
  sessionId: string;
  tenantId: string;
  context: string;
  lastCustomerMessage: string;
  proposedAiResponse: string;
  /** Bot whose consultation this is — needed for the "Let bot continue" call. */
  botId: string;
  timestamp: string;
  /**
   * 2026-10-28 - why the bot consulted (`ConsultationRequested.Reason`, e.g. `CustomerKeyword` when the customer asked
   * for a person). Absent from older hubs and null on the ADR-016 routed card.
   */
  reason?: string | null;
  /**
   * X-Transaction-Id of the per-turn pipeline that raised this consultation.
   * OP-021 (2026-05-06) — used as the dedupe key for the synthetic
   * consultation message so the SignalR-pushed row and the DB-hydrated
   * transcript row collapse to one. One TX = one consultation row.
   */
  transactionId?: string;
  /**
   * ADR-016 §D8 — when targeted routing is ON, the assignee (least-busy
   * eligible agent) chosen by EscalationQueue. Empty/absent in broadcast
   * mode (nobody eligible / routing off). Camel-cased over the wire.
   */
  assignedAgentId?: string;
  /**
   * ADR-016 §D8 — true on the AWARENESS copy delivered to the tenant group
   * for a consultation routed to SOMEONE ELSE; the console renders that copy
   * read-only (claim/answer suppressed). false on the actionable copy sent to
   * the assignee AND on every broadcast-mode copy — i.e. absent/false ⇒
   * today's fully-claimable behavior (routing off).
   */
  assignedToOther?: boolean;
}

/**
 * HC1.D (2026-05-13) — pushed when the bot's guidance examiner judged the
 * teammate's guidance too vague to act on, and composed a clarifying
 * question back to the teammate (rather than guessing and confusing the
 * customer). Renders as a new bot-side message in the existing Bot
 * Consultation pane, under the same session card.
 *
 * The transactionId is the SAME id as the originating ConsultationRequested
 * — lets the consultation card group followups under the right TX without
 * a separate "thread" surface.
 */
export interface BotConsultationFollowupNotification {
  sessionId: string;
  tenantId: string;
  botId: string;
  /** The teammate whose guidance triggered the followup (typically the
   * one the bot wants to hear back from). */
  agentId: string;
  /** The bot's clarifying question — short, in the configured teammate
   * language (HC1.B/C). Customer never sees this. */
  question: string;
  /** Optional rationale from the guidance examiner — secondary text
   * shown beneath the question to explain WHY the bot is asking. */
  reason?: string | null;
  timestamp: string;
  /** Same TX id as the originating ConsultationRequested. */
  transactionId: string;
}

/**
 * HC2.2 / OP-061 (2026-05-27) — pushed when actionable teammate guidance
 * could NOT be delivered to the customer on the PROACTIVE path (no open
 * consultation). The bot's compose step failed (or the content filter
 * rejected the message), so there is no customer-facing reply AND no
 * consultation lifecycle to attach a BotConsultationFollowup to. This
 * event is the live, operator-visible signal that complements the audited
 * WARNING — the operator who just typed the guidance must see immediately
 * that it did not reach the customer, never a silent no-op.
 *
 * The agent-console surfaces it as a distinct role="alert" entry in the
 * Bot Consultation pane and bumps the conversation row's urgency badge.
 * Deduped by transactionId (mirrors the consultation/followup dedupe).
 */
export interface ProactiveGuidanceDeliveryFailedNotification {
  sessionId: string;
  tenantId: string;
  /** The bot that was relaying the guidance. */
  botId: string;
  /** The teammate whose guidance failed to reach the customer. */
  agentId: string;
  /**
   * Short failure code — e.g. <c>compose_failed</c>,
   * <c>compose_failed_with_followup</c>, <c>content_filter_rejected</c>.
   * Mapped to a human-readable line by the store handler.
   */
  reason: string;
  /** X-Transaction-Id of the per-turn pipeline that raised the failure. Dedupe key. */
  transactionId: string;
  timestamp: string;
}

/** ADR-019 D9 — terminal (and one non-terminal) outcomes of a review draft. */
export type EmailDraftOutcome =
  | 'approved'
  | 'edited'
  | 'autoSentMaxWait'
  | 'autoSentIdle'
  | 'cancelled'
  | 'superseded'
  | 'failed'
  | 'rejectedNotOwner';

/**
 * ADR-019 D5/D6 — pushed when the bot drafts (or re-drafts) an Email reply that is gated
 * for human review before it reaches the customer. Mirrors the <c>EmailReplyDraftCreated</c>
 * MassTransit event's SignalR fan-out payload (<c>EmailDraftNotification</c> in
 * <c>IAgentHubClient.cs</c>).
 */
export interface EmailDraftNotification {
  draftId: string;
  sessionId: string;
  tenantId: string;
  draftText: string;
  /** 2026-09-05 - what the customer will receive alongside the text (bank images / files); absent for text-only. */
  attachments?: MessageAttachment[];
  revision: number;
  customerLastMessage: string;
  createdAtUtc: string;
  maxWaitAtUtc: string;
  idleMinutes: number;
  /**
   * OP-582 — `false` when NO elapsed clock can release this draft (the tenant turned
   * auto-send off, or the channel is in Consultation Only mode): only a human action sends
   * it. `undefined` means the field was not on the wire (a service that predates it) and
   * MUST be read as `true`, so a tenant on auto-send sees exactly the behaviour it always had.
   */
  autoSendEnabled?: boolean;
  /** ADR-019 D6 — the agent the session-owner projection resolved, or absent in broadcast mode. */
  assignedAgentId?: string | null;
  /**
   * ADR-019 D6 — true when this copy is a read-only awareness copy (routed to someone
   * else); mirrors {@link ConsultationNotification.assignedToOther}.
   */
  assignedToOther?: boolean;
  /**
   * Why the draft was gated — `"allEmail"` (tenant reviews all email), `"lowCertainty"`
   * (ADR-019 D10), or `"consultOnly"` (OP-579: this CHANNEL never sends on its own, so no timer
   * will ever release the draft — only a person can). Carried as an opaque string; nothing here
   * branches on it, so a new value is additive.
   */
  gateReason?: string;
  /** Set when a Revise re-compose failed and the previous revision is being re-shown. */
  revisionFailureReason?: string | null;
  /** Bot that authored the draft. */
  botId?: string;
  /** Channel the reply will go out on — always "Email" in v1. */
  channel?: string;
  /** X-Transaction-Id of the turn that produced this draft/re-draft. */
  transactionId?: string;
  timestamp?: string;
}

/**
 * ADR-019 D5/D9 — pushed when a review draft reaches a terminal state (or is claimed by
 * another agent — {@link EmailDraftOutcome} 'rejectedNotOwner'). Mirrors
 * <c>EmailDraftResolvedNotification</c> in <c>IAgentHubClient.cs</c>.
 */
export interface EmailDraftResolvedNotification {
  draftId: string;
  sessionId: string;
  tenantId: string;
  outcome: EmailDraftOutcome;
  /** Agent whose action resolved the draft; absent for auto-send / cancellation / supersession. */
  resolvedByAgentId?: string | null;
  transactionId?: string;
  timestamp?: string;
}

/**
 * ADR-019 — the review draft currently pending on a conversation row. Draft state lives
 * HERE (on the row), never in `consultationLog` and never as a transcript message: an
 * unapproved draft has not reached the customer, so it must not look like a sent message.
 */
export interface PendingEmailDraft {
  draftId: string;
  draftText: string;
  /** 2026-09-05 - what the customer will receive alongside the text (bank images / files); absent for text-only. */
  attachments?: MessageAttachment[];
  revision: number;
  customerLastMessage: string;
  createdAtUtc: string;
  maxWaitAtUtc: string;
  idleMinutes: number;
  /**
   * OP-582 — `false` ⇒ this draft can ONLY be released by a person; no timer applies.
   * `undefined` ⇒ unknown (pre-OP-582 server) and treated as `true`, i.e. the countdown
   * the card has always rendered. Use `autoSendEnabled === false` as the hold test —
   * never a falsy check, which would treat "unknown" as "held" and invent a hold that
   * the server never declared.
   */
  autoSendEnabled?: boolean;
  assignedAgentId?: string | null;
  assignedToOther?: boolean;
  revisionFailureReason?: string | null;
  /**
   * Console-local ONLY (never arrives on the wire): set when this console's action came
   * back `rejectedNotOwner` so the card can show a transient "another agent owns this"
   * notice WITHOUT clearing the still-pending draft.
   */
  actionRejected?: boolean;
}

/**
 * ADR-019 — one in-flight draft as served by `GET /api/ai/email-drafts/pending`
 * (the console rehydrate read). Field names mirror {@link EmailDraftNotification}
 * so both wire shapes map through one code path.
 *
 * `assignedToOther` is deliberately ABSENT: the SignalR push computes it per
 * recipient, but this endpoint serves one response to any staff caller, so the
 * console derives it by comparing {@link assignedAgentId} against its own agent id.
 */
export interface PendingEmailDraftDto {
  draftId: string;
  sessionId: string;
  tenantId: string;
  draftText: string;
  /** 2026-09-05 - what the customer will receive alongside the text (bank images / files); absent for text-only. */
  attachments?: MessageAttachment[];
  customerLastMessage: string;
  revision: number;
  createdAtUtc: string;
  maxWaitAtUtc: string;
  idleMinutes: number;
  /**
   * OP-582 — same meaning as on {@link EmailDraftNotification}. Present on BOTH wire shapes
   * deliberately: the console maps them through one code path, and a held draft outlives the
   * connection that first delivered it, so the rehydrated card is the one an agent sees most.
   */
  autoSendEnabled?: boolean;
  assignedAgentId?: string | null;
  gateReason?: string;
  channel?: string;
  botId?: string;
}

export interface MessageNotification {
  messageId: string;
  sessionId: string;
  tenantId: string;
  customerId: string;
  channel: string;
  content: string;
  contentType: string;
  timestamp: string;
  /** WS-E.4 — files attached to this message. Absent is the norm today. */
  attachments?: MessageAttachment[];
}

export interface SlaWarningNotification {
  sessionId: string;
  tenantId: string;
  slaType: string;
  elapsedMinutes: number;
  thresholdMinutes: number;
  timestamp: string;
}

/** Broadcast when a new session is created in the caller's tenant. */
export interface SessionCreatedNotification {
  sessionId: string;
  tenantId: string;
  customerId: string;
  channel: string;
  /** Identified customer's name resolved at session creation; absent for anonymous sessions. */
  customerDisplayName?: string;
  timestamp: string;
}

/**
 * 2026-07-23 — broadcast when a customer's canonical display name resolves or changes in
 * IdentityAuthService AFTER one or more of their sessions were already created. Keyed by
 * customerId (not a single session) since a customer can have multiple open conversations.
 */
export interface CustomerDisplayNameUpdatedNotification {
  tenantId: string;
  customerId: string;
  displayName: string;
  timestamp: string;
}

/** Broadcast when a session's state transitions (e.g., AI_HANDLING -> HUMAN_TAKEOVER). */
export interface SessionStateChangedNotification {
  sessionId: string;
  tenantId: string;
  previousState: string;
  newState: string;
  reason: string;
  timestamp: string;
}

/** Broadcast when a session is closed. */
export interface SessionClosedNotification {
  sessionId: string;
  tenantId: string;
  closedBy: string;
  reason: string;
  timestamp: string;
}

/**
 * OP-1005 — `ReceiveSessionHidden`: a staff member (another agent, or this one) marked an EMAIL
 * conversation as spam / unwanted. Every console of the tenant drops the row at once.
 */
export interface SessionHiddenNotification {
  sessionId: string;
}

/** OP-1005 — `ReceiveSessionRestored`: someone undid a spam mark inside the grace window. */
export interface SessionRestoredNotification {
  sessionId: string;
}

/** OP-1005 — 200 body of POST /api/sessions/{id}/spam. `alreadyMarked` = idempotent repeat. */
export interface SpamMarkResponse {
  sessionId: string;
  purgeAfterUtc: string;
  alreadyMarked: boolean;
}

/** OP-1005 — one row of GET /api/sessions/spam (the "Marked as spam" view). */
export interface SpamMarkedSessionDto {
  sessionId: string;
  customerDisplayName?: string | null;
  subject?: string | null;
  firstMessagePreview?: string | null;
  lastMessageAt?: string | null;
  markedAtUtc: string;
  markedBy?: string | null;
  purgeAfterUtc: string;
  blockSender: boolean;
}

/**
 * ADR-017 D2 — broadcast when a session's per-tenant Category (conversation
 * state) changes. Moves the conversation between console directory sections
 * live (mirrors the SessionStateChanged fan-out). <c>category</c> is the new
 * category key (null ⇒ uncategorized); the store updates the row's category and
 * re-sorts. Independent of the lifecycle <c>state</c> axis — never a transition.
 */
export interface SessionCategoryChangedNotification {
  sessionId: string;
  tenantId: string;
  category: string | null;
  previousCategory?: string | null;
  /** Actor: userId, or "crm-sync" for inbound-origin changes. */
  changedBy?: string;
  timestamp: string;
}

/**
 * Broadcast when an OUTBOUND message (AI bot reply or agent-authored reply)
 * is delivered to the customer. Complements {@link MessageNotification} which
 * only carries INBOUND (customer) messages. Drives the "agent sees the AI's
 * replies and their own replies in real time" behavior — without this handler
 * the agent had to refresh to see anything except the customer side.
 */
export interface OutboundMessageNotification {
  messageId: string;
  sessionId: string;
  tenantId: string;
  /** "ai" or "agent" — drives the sender label in the message bubble. */
  senderType: string;
  senderId: string;
  senderName: string;
  channel: string;
  content: string;
  contentType: string;
  timestamp: string;
  /** WS-E.4 — files attached to this message. Absent is the norm today. */
  attachments?: MessageAttachment[];
}

// ─── API Responses ───────────────────────────────────────────────

export interface ApiError {
  status: number;
  message: string;
  traceId?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// ─── ConversationStoreService DTOs ───────────────────────────────
// Mirrors MessageResponse + TranscriptPageResponse returned by
// GET /api/store/conversations/{sessionId}/messages.

/** A single persisted message as returned by the transcript store. */
export interface StoredMessageDto {
  /** Operator 2026-11-02: the composing bot's public name (outbound bot rows only). */
  senderName?: string | null;
  messageId: string;
  sessionId: string;
  tenantId: string;
  customerId?: string | null;
  channel: string;
  /** "INBOUND" (customer -> system) or "OUTBOUND" (system -> customer). */
  direction: string;
  content: string;
  contentType: string;
  createdAt: string;
  /** WS-E.4 — files attached to this message. Absent is the norm today. */
  attachments?: MessageAttachment[];
  /**
   * OP-1041 - who actually wrote this row, derived by the server from the stored row: `customer` |
   * `bot` | `agent`. Present on the STAFF route only (absent for any other caller), and absent on a
   * server that predates it - which must read as "unknown", never as `bot`.
   */
  senderKind?: 'customer' | 'bot' | 'agent' | string;
  /**
   * The staff user id behind a `senderKind: 'agent'` row (id only; the console resolves the name from
   * the agent directory). Null/absent when the stored row did not record one.
   */
  agentId?: string | null;
  /**
   * 2026-10-28 - on a `consultation-bot` row only: the replies the bot proposes for the customer (staff route only).
   * Null/absent = none (or a server that predates the field).
   */
  proposedReplies?: string[] | null;
  /**
   * 2026-10-31 - the X-Transaction-Id of the turn that wrote this row (staff route only). Shown on the contact card so an
   * admin can paste it into the Logs page. Null/absent on a server that predates the field.
   */
  transactionId?: string | null;
  /** 2026-11-01 - the page the customer was on (customer rows, staff route only). Client claim, display only. */
  pagePath?: string | null;
  /** 2026-11-01 - the page title the customer saw (customer rows, staff route only). Client claim, display only. */
  pageTitle?: string | null;
}

/** Paged envelope returned by the store's GET messages endpoint. */
export interface TranscriptPageResponse {
  sessionId: string;
  messages: StoredMessageDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// ─── Chat action timeline (staff-only) ───────────────────────────
// Mirrors TimelinePageResponse (GET /api/store/conversations/{sessionId}/timeline) and the live
// TimelineEntryNotification pushed as `ReceiveTimelineEntry`. Ids only on the wire - names are
// resolved here from the agent directory, so a renamed or deleted teammate never rewrites history.

/** Who performed a timeline action. `unknown` is a real answer, not a gap to fill with a guess. */
export type TimelineActorKind = 'agent' | 'bot' | 'system' | 'customer' | 'unknown';

/** The documented entry types. The server may add more - every consumer must tolerate an unknown string. */
export type KnownTimelineType =
  | 'consultation_requested'
  | 'consultation_assigned'
  | 'guidance_sent'
  | 'takeover'
  | 'release'
  | 'consultation_dismissed'
  | 'consultation_expired'
  | 'consultation_closed_customer'
  | 'sla_breached'
  | 'session_closed'
  | 'session_marked_spam'
  | 'session_spam_undone'
  | 'email_draft_resolved'
  | 'returned_to_team'
  | 'tags_changed';

/** One REST entry as returned by the timeline route. */
export interface TimelineEntryDto {
  /** Stable id of the stored row - the de-duplication key; a live push for the same entry carries the same value. */
  entryId: string;
  sessionId: string;
  /** A {@link KnownTimelineType}, or any string the server adds later. */
  type: string;
  occurredAtUtc: string;
  actor?: { kind?: string | null; id?: string | null } | null;
  /** Fixed keys per type; every value is a string. */
  params?: Record<string, string> | null;
  /** English one-liner. A fallback for an UNKNOWN type only - known types render from type + params. */
  summary?: string | null;
  fidelity?: string | null;
}

/** Page envelope of the timeline route. Newest page first; entries oldest-first within a page. */
export interface TimelinePageResponse {
  sessionId: string;
  entries: TimelineEntryDto[];
  hasMore: boolean;
  nextBefore?: string | null;
  pageSize: number;
}

/** `ReceiveTimelineEntry` - the live push (camel-cased over the wire). Best effort; a reload is authoritative. */
export interface TimelineEntryNotification {
  entryId: string;
  sessionId: string;
  tenantId?: string;
  type: string;
  occurredAtUtc: string;
  actorKind?: string | null;
  actorId?: string | null;
  params?: Record<string, string> | null;
  summary?: string | null;
  transactionId?: string;
  timestamp?: string;
}

/** A timeline entry in the shape the console holds and renders - REST and push both normalise to this. */
export interface TimelineEntry {
  entryId: string;
  sessionId: string;
  type: string;
  /** ISO-8601 UTC, always with a zone designator. */
  occurredAtUtc: string;
  actorKind: TimelineActorKind;
  actorId: string | null;
  params: Record<string, string>;
  summary: string;
}

// ─── SessionManagementService DTOs ───────────────────────────────
// Mirrors SessionResponse + PagedSessionsResponse returned by GET /api/sessions.

/** Session row as returned by the backend session-management service. */
export interface SessionDto {
  sessionId: string;
  // TenantId removed from the wire response 2026-08-09 (gratuitous tenant-identity
  // leak closure) — every session list request is already scoped to the caller's
  // own tenant (JWT claim), so mapSessionToConversation sources it from authStore
  // instead of the wire.
  customerId: string;
  /** Identified customer's name (W8); null for anonymous. */
  customerDisplayName?: string | null;
  /**
   * Staff-set conversation name; null when nobody has named it — the normal case, and NOT an
   * error. Carried into <see cref="Conversation.subject"/>; the UI falls back to its derived
   * per-channel label when absent.
   */
  subject?: string | null;
  channelType: string;
  // channelConfigId removed from the wire response 2026-08-09 (Tier 1 leak closure) —
  // verified unread anywhere in this app; useAgentHub/JoinSession never sourced it
  // from here.
  state: string;
  assignedBotId?: string | null;
  assignedAgentId?: string | null;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  closeReason?: string | null;
  lastMessageAt?: string | null;
  lastMessagePreview?: string | null;
  /** First message preview (W8), for "Anonymous - {first message}" labels. */
  firstMessagePreview?: string | null;
  /**
   * ADR-017 D3 — the session's per-tenant conversation-state (Category) key, or
   * null when uncategorised. Carried into <see cref="Conversation.category"/> by
   * mapSessionToConversation to drive the console directory grouping.
   */
  category?: string | null;
  messageCount: number;

  // CONTRACT-C additive fields (ADR-033, PR-A1/B1). Every one is OPTIONAL on purpose: a server that predates them
  // simply omits them, and mapSessionToConversation reads absence as "the server did not say" (fail closed), never
  // as a default that grants anything.
  /** The private owner's staff id; null = nobody owns it. */
  ownerAgentId?: string | null;
  ownerSince?: string | null;
  /** 'guidance' | 'takeover' | 'draft_approve' | 'backfill'. */
  ownerSource?: string | null;
  /** The owner released the live conversation back to the bot but kept it. */
  ownerReleased?: boolean;
  ownerVersion?: number;
  /** Is private ownership in effect (false while only PR-A1 is deployed). `null` only on a customer-actor response. */
  ownershipEnforced?: boolean | null;
  /** Catalog tag keys; never null on the wire (absent only from a pre-PR-B1 server). */
  tags?: string[];
  tagsVersion?: number;
  lastActivityAt?: string | null;
  lastCustomerMessageAt?: string | null;
  lastOutboundAt?: string | null;
  /** The customer's previous CLOSED conversation (D7), server-resolved. */
  previousSessionId?: string | null;

  // Conversation-status overhaul. All optional: an old row omits them (priority then reads MEDIUM).
  priority?: ConversationPriority | string | null;
  priorityPinned?: boolean;
  lastPreview?: string | null;
  lastPreviewAt?: string | null;
  lastPreviewSender?: PreviewSender | string | null;
  lastPreviewAgentName?: string | null;
  reopenedAt?: string | null;
  reopenCount?: number;
}

/** Paged envelope returned by GET /api/sessions (legacy page mode: page/pageSize/totalCount are meaningful). */
export interface PagedSessionsResponse {
  items: SessionDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
}

/**
 * A folder id, exactly as the server spells it (case-sensitive):
 * mine | my-consult | my-takeover | my-released | all-open | cat:<key> | tag:<key>`. `drafts` is NOT a list folder
 * (OP-582) - it comes from the pending-drafts feed. The retired `waiting | all-shared | teammates` are an alias of
 * `mine` on the server and are never sent by this console.
 */
export type FolderId = string;

/**
 * Keyset page returned by GET /api/sessions when `folder`, `cursor` or `q` is given. `nextCursor` is opaque (pass it
 * back unchanged); `hasMore` is the only "is there another page" answer; `totalCount` is -1 here and must not be shown.
 * `ownershipEnforced` is the tenant-wide wire flag the console keys its privacy rendering on.
 */
export interface FolderPageResponse {
  items: SessionDto[];
  nextCursor: string | null;
  hasMore: boolean;
  ownershipEnforced: boolean;
}

/** GET /api/sessions/counts?groupBy=folder. The fixed ids (My work + Team) are always present; `cat:`/`tag:` only when non-empty. */
export interface FolderCountsResponse {
  counts: Record<FolderId, number>;
}

/** One catalog tag as the console reads it (GET /api/sessions/tags). The console never creates/edits/deletes one (D8). */
export interface TagDef {
  key: string;
  label: string;
  color: string;
  isActive: boolean;
}

/** POST|DELETE /api/sessions/{id}/tags/{key} (idempotent): the post-image of the session's tag set. */
export interface SessionTagsResponse {
  sessionId: string;
  tags: string[];
  tagsVersion: number;
}

// CONTRACT-C hub pushes (pinned against __tests__/fixtures/agenthub-contract.json).

/** Why ownership changed. The server may add more - treat an unknown string as "re-read the row". */
export type SessionOwnershipReason =
  | 'guidance'
  | 'takeover'
  | 'draft_approve'
  | 'release'
  | 'returned_to_team'
  | 'backfill';

/** `ReceiveSessionOwnershipChanged`. Delivered to the new owner, the previous owner and elevated viewers. */
export interface SessionOwnershipChangedNotification {
  sessionId: string;
  tenantId: string;
  ownerAgentId: string | null;
  previousOwnerAgentId: string | null;
  ownerReleased: boolean;
  /** One of {@link SessionOwnershipReason}, or any string the server adds later (the wire type is a plain string). */
  reason: string;
  /** The session's lifecycle state after the change, or null when the server did not say. */
  state: string | null;
  ownerVersion: number;
  timestamp: string;
}

/** `ReceiveSessionWithdrawn` - content-free: someone claimed a conversation this console may have been showing. */
export interface SessionWithdrawnNotification {
  sessionId: string;
  tenantId: string;
}

/** `ReceiveSessionShared` - content-free: an owner was cleared, the conversation is team-shared again. */
export interface SessionSharedNotification {
  sessionId: string;
  tenantId: string;
}

/** `ReceiveSessionTagsChanged`. Apply only when `tagsVersion` is newer than the row's. */
export interface SessionTagsChangedNotification {
  sessionId: string;
  tenantId: string;
  tags: string[];
  tagsVersion: number;
  changedBy: string;
  timestamp: string;
}

/** `ReceiveTagCatalogChanged` - refetch the catalog. */
export interface TagCatalogChangedNotification {
  tenantId: string;
}

/**
 * `ReceiveConsoleConfigChanged` - content-free: the tenant's console settings or its conversation statuses (state defs)
 * changed in the Admin Portal; refetch BOTH. Carries no setting, so nothing is ever applied from the push itself.
 */
export interface ConsoleConfigChangedNotification {
  tenantId: string;
}

/**
 * GET /api/sessions/console-settings - the tenant's Agent Console settings as the SERVER resolved them.
 * `privateConversations` is the tenant's stored choice (null = inherit the platform default);
 * `privateConversationsEffective` is what the server enforces now. The console renders only display preferences from
 * this (`hideTagFolders`); it never derives visibility from it - the list endpoint and `ownershipEnforced` do that.
 */
export interface ConsoleSettings {
  tenantId: string;
  privateConversations: boolean | null;
  privateConversationsEffective: boolean;
  privateConversationsInherited: boolean;
  hideTagFolders: boolean;
  /** The tenant lets every agent edit tags, status, subject and CRM ticket properties of a CLOSED conversation. A display hint: the server decides. */
  editClosedConversations: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** `ReceiveSessionSubjectChanged` (IAgentHubClient.cs SessionSubjectChangedNotification). `subject` null = CLEARED. */
export interface SessionSubjectChangedNotification {
  sessionId: string;
  tenantId: string;
  subject: string | null;
  previousSubject: string | null;
  changedBy?: string;
  timestamp?: string;
}

/** Answer of the `ReturnToTeam(sessionId)` hub invoke. `stateAfter` can stay HUMAN_CONTROLLED when another agent holds live control. */
export interface ReturnToTeamResult {
  outcome: string;
  stateAfter: string | null;
}

// ─── CRM (HubSpot) interaction history ───────────────────────────
// Mirrors GET /api/crm/customers/{customerId}/interactions →
//   { customerId, interactions: [{ interactionId, type, subject, status, timestamp, description }] }
// Read-only, fail-soft: consumed by CustomerHistoryPanel. All fields are best
// effort — a partial/empty payload must never crash the panel.

/** A single past CRM interaction (ticket / email / note) for a customer. */
export interface Interaction {
  interactionId: string;
  /** CRM interaction kind (e.g. "TICKET", "EMAIL", "NOTE", "CALL"). Free-form. */
  type: string;
  subject: string;
  /** CRM status label (e.g. "OPEN", "CLOSED"). Free-form. */
  status: string;
  /** ISO 8601 timestamp of the interaction. */
  timestamp: string;
  description: string;
}

/** Envelope returned by GET /api/crm/customers/{customerId}/interactions. */
export interface InteractionHistory {
  customerId: string;
  interactions: Interaction[];
}

// ─── Learning-Tag DTOs ────────────────────────────────────────────
// Mirrors the SessionManagementService learning-tag contract.

/**
 * The three learning dispositions an agent can mark on a session.
 * "default"     — normal flywheel handling (synthesize if high-signal).
 * "skip"        — do NOT synthesize / learn from this session.
 * "force-learn" — synthesize even if low-signal or anonymous,
 *                 treating the human operator's note as an authoritative directive.
 */
export type LearningTag = 'default' | 'skip' | 'force-learn';

/** Request body for POST /api/sessions/{sessionId}/learning-tag */
export interface SetLearningTagRequest {
  tag: LearningTag;
  note?: string;
}

/** Response body for POST /api/sessions/{sessionId}/learning-tag */
export interface SetLearningTagResponse {
  sessionId: string;
  tag: LearningTag;
  note?: string | null;
}

/**
 * Response body for GET /api/sessions/{sessionId}/learning-tag.
 * The server answers `{ tag: 'default', note: null }` for an untagged or
 * unknown session rather than 404, so the read path never needs to branch.
 */
export interface LearningTagResponse {
  tag: LearningTag;
  note?: string | null;
}

/** One row in the GET /api/sessions/learning-tagged response list. */
export interface LearningTaggedSessionRow {
  sessionId: string;
  tag: LearningTag;
  note?: string | null;
  customerId: string;
  assignedAgentId?: string | null;
  lastActivityAt: string;
}

// ─── NotificationService DTOs ─────────────────────────────────────
// Mirrors NotificationPreferenceResponse / UpdatePreferencesRequest returned
// by GET/PUT /api/notifications/{agentId}/preferences. Currently only
// EmailEnabled gates a live transport (the escalation-assigned "customer
// needs help" email); Push/InApp have no transport yet and are not
// surfaced by NotificationsSettingsPage.

/** An agent's own notification preference set. */
export interface NotificationPreferences {
  id: string;
  tenantId: string;
  agentId: string;
  emailEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  slaWarningEnabled: boolean;
  slaBreachEnabled: boolean;
  escalationEnabled: boolean;
  systemEnabled: boolean;
}

// ─── EscalationQueueService DTOs (Live Queue view) ────────────────
// Mirrors EscalationItemResponse / QueueStatusResponse.

/** One active escalation item returned by GET /api/escalation/queue. */
export interface EscalationItemDto {
  id: string;
  sessionId: string;
  customerId: string;
  /** EscalationPriority — LOW | NORMAL | HIGH | URGENT (raw enum string). */
  priority: string;
  /** EscalationStatus — PENDING | ASSIGNED | IN_PROGRESS | RESOLVED | EXPIRED. */
  status: string;
  assignedAgentId?: string | null;
  reason: string;
  /** Absolute SLA deadline; wait time is derived from createdAt → now. */
  slaDeadlineAt: string;
  createdAt: string;
  resolvedAt?: string | null;
  /**
   * OP-957 (2026-09-30) — the consultation payload, so a console that starts AFTER the consultation was
   * raised can rebuild the card the live push would have shown. Optional: older servers omit them.
   */
  transactionId?: string;
  botId?: string;
  context?: string;
  lastCustomerMessage?: string;
  proposedAiResponse?: string;
  /**
   * OP-1516 (2026-10-27) — the LATEST ask's note alone and when it was made. `context` keeps every ask of a re-asked item
   * merged ("--- Asked again ---"); the consultation thread shows each ask on its own, at its own time, from these.
   * Optional: an older server omits them.
   */
  latestAskContext?: string;
  latestAskAt?: string | null;
}

/** Per-status counts returned by GET /api/escalation/status. */
export interface QueueStatusResponse {
  pending: number;
  assigned: number;
  inProgress: number;
  resolved: number;
  expired: number;
  total: number;
}

/** Request body for POST /api/escalation/{id}/assign — claim an item. */
export interface AssignEscalationRequest {
  agentId: string;
}

// ─── HumanAgentHubService team-presence DTO (Team view) ───────────
// Mirrors AgentSessionResponse from GET /api/agents/online.

export interface AgentSessionDto {
  id: string;
  agentId: string;
  connectionId: string;
  /** ONLINE | BUSY | AWAY | OFFLINE (raw, lowercase-normalized on the hub). */
  availability: string;
  connectedAt: string;
  lastHeartbeatAt: string;
}

// ─── CrmIntegrationService DTOs (Customer context panel) ──────────
// Mirrors CustomerProfile / InteractionHistory. Fields the design wants but
// the CRM does NOT provide (plan/LTV/orders/tags/sentiment) are intentionally
// absent — the panel renders them as "not available" rather than fabricating.

export interface CustomerProfileDto {
  customerId: string;
  externalContactId?: string | null;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  /**
   * FREE-TEXT company from the CRM contact's own company property — a string a human typed on a
   * contact record, not a link to a Company record. Kept as the fallback; prefer `companyName`
   * whenever it is present.
   */
  company?: string | null;
  /**
   * The resolved CRM Company record id (CRM v2 WS-4.4 / WS-6.2).
   *
   * ⚠️ NOT SERVED YET — these four fields are the shape this panel consumes once the backend
   * `Models/Responses/CustomerProfile.cs` is extended. Today the resolved company reaches ONLY the
   * `CrmContextReady` event: `CrmContextPrefetchService` hands its `CrmCompanyScopedResolution` to
   * the event publisher and never persists it, so neither read endpoint carries it. Every consumer
   * null-checks, so until then they are simply absent and the UI falls back to `company`.
   */
  crmCompanyId?: string | null;
  /** Display name of the resolved Company record. Null when `crmCompanyId` is null. */
  companyName?: string | null;
  /**
   * Which route resolved the company (`cache` | `companyCache` | `companySearch` | …), or the
   * outcome when it did not resolve. Carried for support diagnosis: "no company" and "a company we
   * chose not to guess at" are different situations.
   */
  companyResolvedVia?: string | null;
  /** The tenant-MAPPED company context properties — whatever that tenant chose to surface. */
  companyFields?: Record<string, string> | null;
  /**
   * The tenant-MAPPED CONTACT context properties — the contact-side twin of `companyFields`, and
   * like it the tenant's own chosen set rather than a fixed list of ours.
   *
   * Resolved on the same read as everything else here and long since reaching the bot's prompt; it
   * was dropped in the server's mapping to this DTO, so the agent's card never carried it.
   */
  contextFields?: Record<string, string> | null;
  /** Closest proxy for the design's "plan". */
  tier?: string | null;
  fromCache: boolean;
  /**
   * False when the tenant's CRM has no contact for this customer and the server built
   * this profile from our OWN `customer_identities` record instead — the address the
   * inbound email arrived from (or the phone for WhatsApp/SMS) plus the display name.
   * Absent on older responses; treat `undefined` as "linked" so nothing regresses.
   */
  crmLinked?: boolean;
}

export interface CrmInteractionDto {
  interactionId: string;
  /** call | email | chat | case */
  type: string;
  subject: string;
  status: string;
  timestamp: string;
  description?: string | null;
}

export interface InteractionHistoryDto {
  customerId: string;
  interactions: CrmInteractionDto[];
}
